/**
 * @copyright 2024-2026 David Baugh
 * @license Proprietary - All Rights Reserved
 * Unauthorized copying, modification, or distribution is strictly prohibited.
 */

/**
 * EnrollmentRequests - Teacher interface for managing student enrollment requests
 * Displays pending requests and allows approval/denial
 */

import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Alert, AlertDescription } from "../ui/alert";
import {
  UserPlus,
  CheckCircle,
  XCircle,
  Clock,
  Mail,
  User,
  BookOpen,
  AlertCircle,
  Users
} from "lucide-react";

const EnrollmentRequests = () => {
  const [pendingRequests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [processingId, setProcessingId] = useState(null);
  const [success, setSuccess] = useState(null);

  // Fetch pending requests
  const fetchRequests = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/enrollment-requests/teacher/pending`, {
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error('Failed to fetch enrollment requests');
      }

      const data = await response.json();
      setRequests(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRequests();
  }, []);

  const handleApprove = async (requestId, studentName, cohortName) => {
    try {
      setProcessingId(requestId);
      setError(null);

      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/enrollment-requests/${requestId}/approve`, {
        method: 'PUT',
        credentials: 'include'
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to approve request');
      }

      setSuccess(`Approved ${studentName} for ${cohortName}`);

      // Remove approved request from list
      setRequests(prev => prev.filter(r => r.id !== requestId));

      // Auto-hide success message after 3 seconds
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err.message);
    } finally {
      setProcessingId(null);
    }
  };

  const handleDeny = async (requestId, studentName, cohortName) => {
    if (!window.confirm(`Are you sure you want to deny ${studentName}'s request to join ${cohortName}?`)) {
      return;
    }

    try {
      setProcessingId(requestId);
      setError(null);

      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/enrollment-requests/${requestId}/deny`, {
        method: 'PUT',
        credentials: 'include'
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to deny request');
      }

      setSuccess(`Denied ${studentName}'s request for ${cohortName}`);

      // Remove denied request from list
      setRequests(prev => prev.filter(r => r.id !== requestId));

      // Auto-hide success message after 3 seconds
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err.message);
    } finally {
      setProcessingId(null);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="flex justify-center items-center h-32">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <UserPlus className="h-5 w-5" />
              <span>Enrollment Requests</span>
              {pendingRequests.length > 0 && (
                <Badge variant="destructive" className="ml-2">
                  {pendingRequests.length} pending
                </Badge>
              )}
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={fetchRequests}
              disabled={loading}
            >
              Refresh
            </Button>
          </CardTitle>
        </CardHeader>

        <CardContent>
          {error && (
            <Alert variant="destructive" className="mb-4">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {success && (
            <Alert className="mb-4 bg-green-50 border-green-200">
              <CheckCircle className="h-4 w-4 text-green-600" />
              <AlertDescription className="text-green-800">{success}</AlertDescription>
            </Alert>
          )}

          {pendingRequests.length === 0 ? (
            <div className="text-center py-12">
              <Clock className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-700 mb-2">No Pending Requests</h3>
              <p className="text-gray-600">
                You don't have any enrollment requests waiting for approval.
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {pendingRequests.map((request) => (
                <Card key={request.id} className="border-2 border-blue-100 bg-blue-50/50">
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 space-y-3">
                        {/* Student Info */}
                        <div className="flex items-start gap-3">
                          <div className="bg-blue-100 p-2 rounded-full">
                            <User className="h-5 w-5 text-blue-600" />
                          </div>
                          <div className="flex-1">
                            <h4 className="font-semibold text-lg">
                              {request.first_name} {request.last_name}
                            </h4>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm text-gray-600 mt-1">
                              <div className="flex items-center gap-1">
                                <Users className="h-3 w-3" />
                                <span>ID: {request.student_id}</span>
                              </div>
                              {request.email && (
                                <div className="flex items-center gap-1">
                                  <Mail className="h-3 w-3" />
                                  <span>{request.email}</span>
                                </div>
                              )}
                              <div className="flex items-center gap-1">
                                <BookOpen className="h-3 w-3" />
                                <span>Currently in {request.current_classes_count} class(es)</span>
                              </div>
                              <div className="flex items-center gap-1">
                                <Clock className="h-3 w-3" />
                                <span>Requested {new Date(request.requested_at).toLocaleDateString()}</span>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Cohort Info */}
                        <div className="bg-white p-3 rounded-lg">
                          <div className="text-sm font-medium text-gray-700 mb-1">
                            Requesting to join:
                          </div>
                          <div className="font-semibold text-lg text-blue-700">
                            {request.cohort_name}
                          </div>
                          {request.cohort_description && (
                            <div className="text-sm text-gray-600 mt-1">
                              {request.cohort_description}
                            </div>
                          )}
                        </div>

                        {/* Student Message */}
                        {request.message && (
                          <div className="bg-white p-3 rounded-lg border border-gray-200">
                            <div className="text-sm font-medium text-gray-700 mb-1">
                              Message from student:
                            </div>
                            <div className="text-sm text-gray-800 italic">
                              "{request.message}"
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Action Buttons */}
                      <div className="flex flex-col gap-2 shrink-0">
                        <Button
                          onClick={() => handleApprove(
                            request.id,
                            `${request.first_name} ${request.last_name}`,
                            request.cohort_name
                          )}
                          disabled={processingId === request.id}
                          className="bg-green-600 hover:bg-green-700 min-w-[120px]"
                        >
                          {processingId === request.id ? (
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                          ) : (
                            <>
                              <CheckCircle className="h-4 w-4 mr-2" />
                              Approve
                            </>
                          )}
                        </Button>
                        <Button
                          onClick={() => handleDeny(
                            request.id,
                            `${request.first_name} ${request.last_name}`,
                            request.cohort_name
                          )}
                          disabled={processingId === request.id}
                          variant="destructive"
                          className="min-w-[120px]"
                        >
                          <XCircle className="h-4 w-4 mr-2" />
                          Deny
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default EnrollmentRequests;